import { useCallback } from "react"
import { useAudioPlayer } from "@/contexts/audio-player-context"
import type Sample from "@/types/generated/Sample"

export function useSamplePlayer(sample: Sample) {
  const { state, play, pause, seek } = useAudioPlayer()

  const isThisSample = state.currentSampleId === sample.id
  const isPlaying = isThisSample && state.isPlaying
  const isLoading = isThisSample && state.isLoading
  const progress =
    isThisSample && state.duration > 0
      ? state.currentTime / state.duration
      : 0
  const currentTime = isThisSample ? state.currentTime : 0
  const duration = isThisSample && state.duration > 0 ? state.duration : sample.duration ?? 0
  const canPlay = !!sample.stream_url

  const handlePlayPause = useCallback(() => {
    if (!sample.stream_url) return
    if (isPlaying) {
      pause()
    } else {
      play(sample.id, sample.stream_url)
    }
  }, [sample.id, sample.stream_url, isPlaying, play, pause])

  const handleSeek = useCallback(
    (clickProgress: number) => {
      if (!sample.stream_url) return
      if (!isThisSample || state.duration === 0) {
        play(sample.id, sample.stream_url)
        return
      }
      seek(clickProgress * state.duration)
    },
    [sample.id, sample.stream_url, isThisSample, state.duration, play, seek]
  )

  return {
    isPlaying,
    isLoading,
    progress,
    currentTime,
    duration,
    canPlay,
    handlePlayPause,
    handleSeek,
  }
}
